import { useRef, useState, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { 
  fadeInUp, 
  staggerContainer, 
 
} from '../utils/animations';

// Header Component
const Header = () => {
    const headerRef = useRef();
    const [isOpen, setIsOpen] = useState(false);
  
    useEffect(() => {
      gsap.registerPlugin(ScrollTrigger);
      const ctx = gsap.context(() => {
        // Initial header animation
        gsap.from(headerRef.current, {
          y: -100,
          opacity: 0,
          duration: 1,
          ease: "power3.out"
        });
    
        // Floating logo animation
        gsap.to(".logo-float", {
          y: "10px",
          duration: 2,
          ease: "power1.inOut",
          yoyo: true,
          repeat: -1
        });
    
        // Add scroll-triggered header background
        ScrollTrigger.create({
          start: "top-=100",
          onUpdate: (self) => {
            gsap.to(headerRef.current, {
              backgroundColor: `rgba(255, 255, 255, ${self.progress * 0.9})`
            });
          }
        });
      }, headerRef);

      return () => ctx.revert();
    }, []);
  
    return (
      <header 
        ref={headerRef} 
        className="fixed top-0 left-0 w-full z-50 backdrop-blur-sm bg-white/90 shadow-sm pb-15"
      >
        <div className="container mx-auto px-4 py-3 md:py-4">
          <nav className="flex items-center justify-between">
            <motion.a 
              href="/"
              className="logo-float flex-shrink-0"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <img 
                src="/api/placeholder/48/48" 
                alt="JBF Logo" 
                className="h-10 md:h-12"
              />
            </motion.a>
  
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              <NavItems />
            </div>
  
            {/* Mobile Menu Button */}
            <motion.button
              className="lg:hidden p-2"
              onClick={() => setIsOpen(!isOpen)}
              whileTap={{ scale: 0.95 }}
              aria-label="Toggle Mobile Menu"
            >
              <div className="w-6 h-0.5 bg-gray-800 mb-1.5" />
              <div className="w-6 h-0.5 bg-gray-800 mb-1.5" />
              <div className="w-6 h-0.5 bg-gray-800" />
            </motion.button>
          </nav>
        </div>
  
        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && <MobileNav onClose={() => setIsOpen(false)} />}
        </AnimatePresence>
      </header>
    );
  };
  
  // Navigation Items
  const NavItems = () => {
    return (
      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        className="flex items-center space-x-6 xl:space-x-8"
      >
        <NavLink href="#home">Home</NavLink>
        <NavDropdown title="Organization">
          <DropdownItem href="focus.html">JBF@Focus</DropdownItem>
        </NavDropdown>
        <NavLink href="ipr.html">IPR</NavLink>
        <NavLink href="ims.html">IMS</NavLink>
        <NavDropdown title="OUR PROGRAMS">
          <DropdownItem href="sfs.html">Sustainable Futures Society</DropdownItem>
          <DropdownItem href="sec.html">Social Empowerment Centre</DropdownItem>
        </NavDropdown>
        <NavDropdown title="Publication">
          <DropdownItem href="article.html">Articles</DropdownItem>
        </NavDropdown>
        <NavLink href="contact.html">Contact Us</NavLink>
      </motion.div>
    );
  };
  
  // Navigation Link
  const NavLink = ({ href, children }) => (
    <motion.a
      href={href}
      variants={fadeInUp}
      className="text-gray-700 hover:text-amber-500 transition-colors relative py-2"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      {children}
      <motion.div
        className="absolute bottom-0 left-0 w-full h-0.5 bg-amber-500"
        initial={{ scaleX: 0 }}
        whileHover={{ scaleX: 1 }}
        transition={{ duration: 0.2 }}
      />
    </motion.a>
  );
  
  // Dropdown Navigation
  const NavDropdown = ({ title, children }) => {
    return (
      <motion.div 
        className="relative group"
        variants={fadeInUp}
      >
        <motion.button
          className="flex items-center space-x-1 text-gray-700 hover:text-amber-500 transition-colors"
          whileHover={{ scale: 1.05 }}
        >
          <span>{title}</span>
          <ChevronDown className="w-4 h-4" />
        </motion.button>
  
        <motion.div
          className="absolute top-full left-0 hidden group-hover:block bg-white rounded-lg shadow-lg p-2 min-w-[200px] z-50"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {children}
        </motion.div>
      </motion.div>
    );
  };
  
  // Dropdown Item
  const DropdownItem = ({ href, children }) => (
    <motion.a
      href={href}
      className="block px-4 py-2 text-gray-700 hover:text-amber-500 hover:bg-amber-50 rounded-md transition-colors"
      whileHover={{ x: 5 }}
    >
      {children}
    </motion.a>
  );
  
  // Mobile Navigation Link
  const MobileNavLink = ({ href, children }) => (
    <motion.a
      href={href}
      variants={fadeInUp}
      className="block text-gray-700 hover:text-amber-500 transition-colors py-2"
      whileHover={{ x: 5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      {children}
    </motion.a>
  );
  
  // Mobile Navigation Dropdown Component
  const MobileNavDropdown = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(false);
  
    return (
      <motion.div 
        variants={fadeInUp}
        className="w-full"
      >
        <motion.button
          className="flex items-center justify-between w-full text-gray-700 hover:text-amber-500 transition-colors py-2"
          onClick={() => setIsOpen(!isOpen)}
          whileHover={{ x: 5 }}
        >
          <span>{title}</span>
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="w-4 h-4" />
          </motion.div>
        </motion.button>
  
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="pl-4"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: "hidden" }}
            >
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };
  
  // Mobile Navigation Content Component
  const MobileNavItems = () => {
    return (
      <motion.div 
        className="space-y-4"
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
      >
        <MobileNavLink href="#home">Home</MobileNavLink>
        <MobileNavDropdown title="Organization">
          <MobileNavLink href="focus.html">JBF@Focus</MobileNavLink>
        </MobileNavDropdown>
        <MobileNavLink href="ipr.html">IPR</MobileNavLink>
        <MobileNavLink href="ims.html">IMS</MobileNavLink>
        <MobileNavDropdown title="OUR PROGRAMS">
          <MobileNavLink href="sfs.html">Sustainable Futures Society</MobileNavLink>
          <MobileNavLink href="sec.html">Social Empowerment Centre</MobileNavLink>
        </MobileNavDropdown>
        <MobileNavDropdown title="Publication">
          <MobileNavLink href="article.html">Articles</MobileNavLink>
        </MobileNavDropdown>
        <MobileNavLink href="contact.html">Contact Us</MobileNavLink>
      </motion.div>
    );
  };
  
  // Mobile Navigation Menu Component
  const MobileNav = ({ onClose }) => (
    <motion.div
      className="fixed inset-0 bg-black/50 z-50 lg:hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div 
        className="absolute right-0 top-0 h-full w-64 md:w-80 bg-white p-6"
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "tween", duration: 0.3 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-end mb-6">
          <motion.button
            onClick={onClose}
            whileTap={{ scale: 0.95 }}
            className="p-2"
            aria-label="Close Menu"
          >
            <X className="w-6 h-6 text-gray-700" />
          </motion.button>
        </div>
        
        <nav>
          <MobileNavItems />
        </nav>
      </motion.div>
    </motion.div>
  );

export default Header;